import { Injectable, OnDestroy } from "@angular/core";
import { Layout, NxCell, NxCellRows } from "qlik";
import { filter, map, take } from "rxjs/operators";
import { BehaviorSubject, interval, Subject } from "rxjs";
import { IQlikIDConfig, IQlikKpi, IQlikLib, ListLayout } from "../qliklib/qliklib";

export interface IQlikSelection {
  fieldName: string;
  field: object;
  totalCount: number;
  qSelected: string;
  selectedValues: object[];
}

@Injectable({ providedIn: "root" })
export class QlikService implements OnDestroy {
  public objectKeys = Object.keys;
  public qliklib$: BehaviorSubject<IQlikLib> = new BehaviorSubject(null);
  public qlikIdConfig: IQlikIDConfig = null;
  public exportDataListe: { [id: string]: BehaviorSubject<NxCellRows[]> } = {};

  constructor() {
    // Need a waiting loop until Qlik javascript and object is ready for use
    interval(300)
      .pipe(
        filter(() => window["qlikObjects"] !== undefined),
        map(() => window["qlikObjects"]),
        take(1),
      )
      .subscribe((qlikObjects: any) => {
        // Qlik objects found, we can now initialize Qlik
        this.qlikIdConfig = qlikObjects.qlikConfig;

        qlikObjects.qliklib.openQlikApp(this.qlikIdConfig);
        this.qliklib$.next(qlikObjects.qliklib);
      });
  }

  // An observable serving the Qlik Javascript interface when it's available
  public getQliklib(): BehaviorSubject<any> {
    return this.qliklib$;
  }

  // Vis som tabell
  public getTableData(id: string): BehaviorSubject<NxCellRows[]> {
    const exportData$: BehaviorSubject<NxCellRows[]> = new BehaviorSubject<NxCellRows[]>(null);
    this.getQliklib()
      .subscribe((qliklib: IQlikLib) => {
        exportData$.next(qliklib.exportData(id));
      })
      .unsubscribe();
    this.exportDataListe[id] = exportData$;
    return exportData$;
  }

  // Lagre som - Excel fil
  public saveAsExcel(id: string, currentSelection, callback?: (error: any, url: string) => void) {
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        qliklib.saveExportData(id, "OOXML", currentSelection, callback);
      });
  }

  // Lagre som - Csv fil
  public saveAsCsv(id: string, currentSelection, callback?: (error: any, url: string) => void) {
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        qliklib.saveExportData(id, "CSV_C", currentSelection, callback);
      });
  }

  // Setter varible
  public setVariableNum(field: string, value: number) {
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        qliklib.setVariableNum(field, value);
      });
  }

  public setFylkeFilter() {
    this.setVariableNum("vVisKommuner", 0);
  }

  // An observable giving the requested KPI value and title
  public getKpi(kpiId): Subject<IQlikKpi> {
    const list: Subject<IQlikKpi> = new Subject();
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        qliklib.createKpi(kpiId, (result: ListLayout) => {
          list.next(qliklib.getKpiData(result));
        });
      });
    return list;
  }

  public unsubscribeCurrentSelections(id: string): void {
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        qliklib.unsubscribeCurrentSelections(id);
      });
  }

  public subscribeCurrentSelections(id: string, funcToEval: (currentSelection: IQlikSelection) => void): void {
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        qliklib.subscribeCurrentSelections(id, funcToEval);
      });
  }

  // An observable giving a Qlik list of NxCell objects
  public getList(listId, deletedSession?: boolean, excludeCurrentSelection?: boolean, opts?: object): Subject<NxCell[]> {
    const list: Subject<NxCell[]> = new Subject();
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe(qliklib => {
        if (typeof listId === "string") {
          qliklib.createList(listId, opts, (result: ListLayout) => {
            if (deletedSession) {
              qliklib.destroySessionObject(result.qInfo.qId);
            }

            list.next(qliklib.getListData(result));

            setTimeout(() => {
              Object.keys(this.exportDataListe).forEach((id: string) => {
                this.exportDataListe[id].next(qliklib.exportData(id));
              });
            });
          });
        } else {
          // returner todimensjonale date, feks fylke med kommuner
          qliklib.createCube(listId, { excludeCurrentSelection }, (result: Layout) => {
            if (deletedSession) {
              qliklib.destroySessionObject(result.qInfo.qId);
            }

            list.next(qliklib.getCubeData(result));

            setTimeout(() => {
              Object.keys(this.exportDataListe).forEach((id: string) => {
                this.exportDataListe[id].next(qliklib.exportData(id));
              });
            });
          });
        }
      });
    return list;
  }

  // An observable giving a Qlik list of NxCell objects
  public getMultiList(listId: string[]): Subject<NxCell[]> {
    const list: Subject<NxCell[]> = new Subject();
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe(qliklib => {
        // returner todimensjonale date, feks fylke med kommuner
        qliklib.createMultiList(listId, (result: ListLayout[]) => {
          list.next(qliklib.getMultiListData(result));

          setTimeout(() => {
            Object.keys(this.exportDataListe).forEach((id: string) => {
              this.exportDataListe[id].next(qliklib.exportData(id));
            });
          });
        });
      });
    return list;
  }

  // Set a new filter or unselect a current one
  // isMulti - should it be possible to set several parameters on this filter?
  // isLocked - this filter will not be cleared on qliklib.clearSelections()
  public setFilter(id: string, value: string | string[] | number, isMulti: boolean, isLocked = false) {
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        if (isLocked) {
          qliklib.setLockedSelection(id, value, isMulti);
        } else {
          qliklib.selectField(id, value, isMulti);
        }
      });
  }

  // Nullstill filter
  public resetFilter() {
    this.getQliklib()
      .pipe(
        filter(qliklib => qliklib !== null),
        take(1),
      )
      .subscribe((qliklib: IQlikLib) => {
        qliklib.clearSelections();
      });
  }

  // Returnerer liste med valgte filtre
  public hentValgtFilterListe(valgtFilter) {
    const alleValgteFiltre: string[] = [];
    let filterElement: string = null;

    if (valgtFilter) {
      this.objectKeys(valgtFilter).forEach(filterItem => {
        filterElement = filterItem.toLowerCase();
        alleValgteFiltre.push(" " + filterElement + ": " + valgtFilter[filterItem].join(", "));
      });
      return alleValgteFiltre;
    } else {
      return null;
    }
  }

  // Does the specified Qlik graph object id exist?
  public isGrafId(id) {
    if (!this.qlikIdConfig) {
      throw new Error("Error: Qlik ID config is unavailable, Qlik is not yet initialized");
    }
    return this.qlikIdConfig.qlikObjects["qv-graf-" + id] !== undefined;
  }

  // Does the specified Qlik KPI object id exist?
  public isKpiId(id) {
    if (!this.qlikIdConfig) {
      throw new Error("Error: Qlik ID config is unavailable, Qlik is not yet initialized");
    }
    return this.qlikIdConfig.qlikObjects["qv-kpi-" + id] !== undefined;
  }

  public ngOnDestroy() {
    this.qliklib$.next(null);
    this.qliklib$.complete();
  }

  public destroyKpi(kpiId) {
    this.getQliklib().subscribe((qliklib: IQlikLib) => {
      qliklib?.closeKpi(kpiId);
    });
  }
}
